import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Users, Monitor, Eye, UserMinus, DollarSign, ArrowLeft } from 'lucide-react';

interface StatisticsWidgetProps {
  id: string;
  title: string;
  statistic_type: string;
  period: string;
  currency?: string;
  target?: number;
  current_value?: number;
  current_count?: number;
  target_count?: number;
  percentage?: number;
  trend?: number;
  trend_direction?: 'up' | 'down';
}

const StatisticsWidget: React.FC<StatisticsWidgetProps> = ({
  id,
  title,
  statistic_type,
  period,
  currency = 'EUR',
  target = 0,
  current_value = 0,
  current_count = 0,
  target_count = 0,
  percentage = 0,
  trend = 0,
  trend_direction = 'up'
}) => {
  // Get currency symbol
  const getCurrencySymbol = () => {
    switch (currency) {
      case 'USD': return '$';
      case 'GBP': return '£';
      case 'EUR':
      default: return '€';
    }
  };

  // Get icon for statistic type
  const getIcon = () => {
    switch (statistic_type) {
      case 'revenue':
      case 'arpc':
        return <DollarSign className="h-5 w-5" />;
      case 'new_customers':
      case 'active_users':
        return <Users className="h-5 w-5" />;
      case 'website_sessions':
        return <Monitor className="h-5 w-5" />;
      case 'page_views':
        return <Eye className="h-5 w-5" />;
      case 'churn_rate':
        return <UserMinus className="h-5 w-5" />;
      case 'bounce_rate':
        return <ArrowLeft className="h-5 w-5" />;
      default:
        return <TrendingUp className="h-5 w-5" />;
    }
  };

  // Format number with locale
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('nl-NL').format(num);
  };

  // Render trend indicator
  const renderTrend = () => {
    if (trend === 0) return null;

    const isPositive = trend_direction === 'up';
    const isGoodTrend = (['revenue', 'new_customers', 'website_sessions', 'page_views', 'active_users'].includes(statistic_type) && isPositive) ||
                        (['churn_rate', 'bounce_rate'].includes(statistic_type) && !isPositive);

    return (
      <div className={`flex items-center text-sm ${isGoodTrend ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? (
          <TrendingUp className="w-4 h-4 mr-1" />
        ) : (
          <TrendingDown className="w-4 h-4 mr-1" />
        )}
        <span>{isPositive ? '+' : ''}{trend}%</span>
      </div>
    );
  };

  // Render content based on statistic type
  const renderContent = () => {
    switch (statistic_type) {
      case 'revenue':
        return (
          <div className="text-center py-4">
            <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {getCurrencySymbol()}{formatNumber(current_value)}
            </div>
            {renderTrend()}
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Revenue {period}
            </div>
          </div>
        );

      case 'new_customers':
        const newCustomerPercentage = target_count > 0 ? Math.round((current_count / target_count) * 100) : 0;
        return (
          <div className="text-center py-4">
            <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {formatNumber(current_count)} / {formatNumber(target_count)}
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3 mb-2">
              <div
                className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(newCustomerPercentage, 100)}%` }}
              ></div>
            </div>
            <div className="text-blue-600 text-sm font-semibold">
              {newCustomerPercentage}% of target
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {period} goal
            </div>
          </div>
        );

      case 'website_sessions':
        return (
          <div className="text-center py-4">
            <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {formatNumber(current_count)}
            </div>
            {renderTrend()}
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Website sessions {period}
            </div>
          </div>
        );

      case 'page_views':
        const pageViewPercentage = target_count > 0 ? Math.round((current_count / target_count) * 100) : 0;
        return (
          <div className="text-center py-4">
            <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {formatNumber(current_count)}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Target: {formatNumber(target_count)} ({pageViewPercentage}%)
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
              <div
                className="bg-green-600 h-2 rounded-full"
                style={{ width: `${Math.min(pageViewPercentage, 100)}%` }}
              ></div>
            </div>
          </div>
        );

      case 'churn_rate':
        return (
          <div className="text-center py-4">
            <div className="text-3xl font-bold text-red-600 mb-2">
              {percentage}%
            </div>
            {renderTrend()}
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Customer churn rate {period}
            </div>
          </div>
        );

      case 'active_users':
        return (
          <div className="text-center py-4">
            <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {formatNumber(current_count)}
            </div>
            {renderTrend()}
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Active users {period}
            </div>
          </div>
        );

      case 'arpc':
        const arpcPercentage = target > 0 ? Math.round((current_value / target) * 100) : 0;
        return (
          <div className="text-center py-4">
            <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {getCurrencySymbol()}{formatNumber(current_value)}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Target: {getCurrencySymbol()}{formatNumber(target)} ({arpcPercentage}%)
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
              <div
                className="bg-orange-600 h-2 rounded-full"
                style={{ width: `${Math.min(arpcPercentage, 100)}%` }}
              ></div>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Average Revenue Per Customer
            </div>
          </div>
        );

      case 'bounce_rate':
        return (
          <div className="text-center py-4">
            <div className="text-3xl font-bold text-orange-600 mb-2">
              {percentage}%
            </div>
            {renderTrend()}
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Bounce rate {period}
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-4">
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {formatNumber(current_value || current_count)}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              {title}
            </div>
          </div>
        );
    }
  };

  return (
    <Card className="bg-transparent border-none shadow-none">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium flex items-center">
          {getIcon()}
          <span className="ml-2">{title}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {renderContent()}
      </CardContent>
    </Card>
  );
};

export default StatisticsWidget;
